// Treasury deposit-requests (recharge) module parser — DTO (wire
// shape) → view model. The seam every consumer must respect.
// Containers / components / composables never read a snake_case field
// directly.

import type {
  DepositRequestAuditDto,
  DepositRequestDetailDto,
  DepositRequestInvestorDto,
  DepositRequestProofDto,
  DepositRequestRowDto,
  DepositRequestStatusCountsDto,
  DepositRequestTreasuryAccountDto,
  DepositRequestsListResponseDto,
} from './depositRequestsContracts'
import type {
  DepositRequestAudit,
  DepositRequestDetail,
  DepositRequestInvestor,
  DepositRequestProof,
  DepositRequestRow,
  DepositRequestStatusCounts,
  DepositRequestTreasuryAccount,
  DepositRequestsListResponse,
} from '../types/depositRequestsTypes'

export function parseDepositRequestRow(dto: DepositRequestRowDto): DepositRequestRow {
  return {
    id: String(dto.id ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    fiatWalletId: String(dto.fiatWalletId ?? dto.fiat_wallet_id ?? ''),
    status: dto.status,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    paymentReference: String(dto.paymentReference ?? dto.payment_reference ?? ''),
    utrNumber: dto.utrNumber ?? dto.utr_number ?? null,
    transactionDocument: dto.transactionDocument ?? dto.transaction_document ?? null,
    approvedBankReference: dto.approvedBankReference ?? dto.approved_bank_reference ?? null,
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
    completedAt: dto.completedAt ?? dto.completed_at ?? null,
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    investorName: String(dto.investorName ?? dto.investor_name ?? ''),
    investorEmail: String(dto.investorEmail ?? dto.investor_email ?? ''),
  }
}

export function parseDepositRequestsListResponse(
  dto: DepositRequestsListResponseDto,
): DepositRequestsListResponse {
  const limit = Number(dto.meta?.limit ?? 0)
  const total = Number(dto.meta?.total ?? 0)
  const page = Number(dto.meta?.page ?? 1)
  const totalPagesRaw = dto.meta?.totalPages ?? dto.meta?.total_pages
  const totalPages =
    totalPagesRaw !== undefined && totalPagesRaw !== null
      ? Number(totalPagesRaw)
      : limit > 0
        ? Math.ceil(total / limit)
        : 0
  return {
    data: (dto.data ?? []).map(parseDepositRequestRow),
    meta: { total, page, limit, totalPages },
  }
}

export function parseDepositRequestStatusCounts(
  dto: DepositRequestStatusCountsDto,
): DepositRequestStatusCounts {
  return {
    pendingVerification: Number(dto.pendingVerification ?? dto.pending_verification ?? 0),
    completed: Number(dto.completed ?? 0),
    rejected: Number(dto.rejected ?? 0),
  }
}

function parseInvestor(dto: DepositRequestInvestorDto): DepositRequestInvestor {
  return {
    id: String(dto.id ?? ''),
    firstName: dto.firstName ?? dto.first_name ?? null,
    lastName: dto.lastName ?? dto.last_name ?? null,
    email: String(dto.email ?? ''),
    kycStatus: dto.kycStatus ?? dto.kyc_status ?? null,
  }
}

function parseProof(dto: DepositRequestProofDto): DepositRequestProof {
  return {
    paymentReference: String(dto.paymentReference ?? dto.payment_reference ?? ''),
    documentUrl: dto.documentUrl ?? dto.document_url ?? null,
    submittedAt: String(dto.submittedAt ?? dto.submitted_at ?? ''),
  }
}

function parseTreasuryAccount(
  dto: DepositRequestTreasuryAccountDto,
): DepositRequestTreasuryAccount {
  return {
    id: String(dto.id ?? ''),
    accountName: String(dto.accountName ?? dto.account_name ?? ''),
    bankName: String(dto.bankName ?? dto.bank_name ?? ''),
    iban: dto.iban ?? null,
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
  }
}

function parseAudit(dto: DepositRequestAuditDto | undefined): DepositRequestAudit {
  return {
    approvedBy: dto?.approvedBy ?? dto?.approved_by ?? null,
    approvedAt: dto?.approvedAt ?? dto?.approved_at ?? null,
    approvedBankReference: dto?.approvedBankReference ?? dto?.approved_bank_reference ?? null,
  }
}

export function parseDepositRequestDetail(dto: DepositRequestDetailDto): DepositRequestDetail {
  const treasury = dto.treasuryBankAccount ?? dto.treasury_bank_account
  return {
    id: String(dto.id ?? ''),
    investorId: String(dto.investorId ?? dto.investor_id ?? ''),
    fiatWalletId: String(dto.fiatWalletId ?? dto.fiat_wallet_id ?? ''),
    status: dto.status,
    amount: String(dto.amount ?? '0'),
    currencyCode: String(dto.currencyCode ?? dto.currency_code ?? ''),
    rejectionReason: dto.rejectionReason ?? dto.rejection_reason ?? null,
    createdAt: String(dto.createdAt ?? dto.created_at ?? ''),
    completedAt: dto.completedAt ?? dto.completed_at ?? null,
    investor: parseInvestor(dto.investor),
    proof: parseProof(dto.proof),
    treasuryBankAccount: treasury ? parseTreasuryAccount(treasury) : null,
    audit: parseAudit(dto.audit),
  }
}
